import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom-v5-compat";
import { IconNameEnum } from "@b2bportal/core-themes";
import { DateTimeFormatStyle, Trans, useI18nContext } from "@hopper-b2b/i18n";
import { Button, ButtonTypeEnum, Icon, Pill, PillTone } from "@tenant/ui";
import dayjs from "dayjs";
import { OfferDetailsDialog } from "../OfferDetailsDialog";
import defaultHeroImage from "@/assets/images/bonus-earn-dialog-hero.webp";
import {
  HOTEL_3X_EARN_OFFER_NAME,
  IsOfferClaimed,
  OfferFunnelEntrySource,
  OfferRawEventName,
  OfferScreen,
  OfferType,
  HOTEL_3X_EARN_OFFER_ID,
  setOfferFunnelEntrySource,
  useEmitRbcRawEvent,
} from "@/tracking/offers";
import { HOTELS_PATH } from "@/utilities/constants";

export interface BonusEarnDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bonusPointsEarnRate: number;
  validUntil?: string;
  imageUrl?: string;
  /** Screen the parent banner renders on — segments the modal events. */
  screen?: OfferScreen;
}

export const BonusEarnDialog = ({
  open,
  onOpenChange,
  bonusPointsEarnRate,
  validUntil,
  imageUrl,
  screen = OfferScreen.home,
}: BonusEarnDialogProps) => {
  const { t, formatDateTime } = useI18nContext();
  const navigate = useNavigate();

  const emitViewedModal = useEmitRbcRawEvent(
    OfferRawEventName.ViewedOfferDetailsModal
  );
  const emitTappedCta = useEmitRbcRawEvent(
    OfferRawEventName.TappedOfferModalCta
  );

  // Fire once per open cycle (dialog can also open via the `#offer` deeplink).
  const hasEmittedViewRef = useRef(false);
  useEffect(() => {
    if (open && !hasEmittedViewRef.current) {
      hasEmittedViewRef.current = true;
      emitViewedModal({
        properties: {
          offer_type: OfferType.rbc_funded_points,
          offer_id: HOTEL_3X_EARN_OFFER_ID,
          offer_name: HOTEL_3X_EARN_OFFER_NAME,
          screen,
          points_multiplier: bonusPointsEarnRate,
          offer_valid_until: validUntil,
          is_offer_claimed: IsOfferClaimed.not_applicable,
          is_claimable: false,
        },
      });
    } else if (!open) {
      hasEmittedViewRef.current = false;
    }
  }, [open, screen, bonusPointsEarnRate, validUntil, emitViewedModal]);

  // Non-home surfaces have no CTA, so attribute on open. Home attributes on the
  // CTA tap (handleSearchHotels).
  useEffect(() => {
    if (open && screen !== OfferScreen.home) {
      setOfferFunnelEntrySource(OfferFunnelEntrySource.offer_banner);
    }
  }, [open, screen]);

  const handleSearchHotels = () => {
    emitTappedCta({
      properties: {
        offer_type: OfferType.rbc_funded_points,
        offer_id: HOTEL_3X_EARN_OFFER_ID,
        offer_name: HOTEL_3X_EARN_OFFER_NAME,
        screen,
        is_claimable: false,
      },
    });
    // Attribute the funnel entry to this offer banner.
    setOfferFunnelEntrySource(OfferFunnelEntrySource.offer_banner);
    onOpenChange(false);
    navigate(HOTELS_PATH, { state: { fromOfferBanner: true } });
  };

  const title = t("bonusEarnBanner.dialog.title", {
    earnRate: bonusPointsEarnRate,
  });
  const offerEndDate =
    validUntil != null
      ? formatDateTime(
          dayjs(validUntil).toDate(),
          DateTimeFormatStyle.Custom({ month: "short", day: "numeric" })
        )
      : undefined;
  const heroImage = imageUrl?.startsWith("https://")
    ? imageUrl
    : defaultHeroImage;

  return (
    <OfferDetailsDialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      heroImageSrc={heroImage}
      details={(cn) => (
        <>
          <div className={cn("section", ["heading"])}>
            {/* Desktop heading carries the asterisk that anchors the cap disclaimer; mobile does not (Figma 36018-138836). */}
            <h2 className={cn("title", ["mobile-only"])}>{title}</h2>
            <h2 className={cn("title", ["desktop-only"])}>
              {t("bonusEarnBanner.dialog.titleDesktop", {
                earnRate: bonusPointsEarnRate,
              })}
            </h2>
            <Pill className={cn("badge")} size="custom" tone={PillTone.custom}>
              {t("bonusEarnBanner.dialog.badge", {
                earnRate: bonusPointsEarnRate,
              })}
            </Pill>
          </div>
          {validUntil != null && (
            <div className={cn("section")}>
              <h3 className={cn("section-title")}>
                {t("bonusEarnBanner.dialog.validUntil")}
              </h3>
              <div className={cn("valid-until")}>
                <Icon iconName={IconNameEnum.timer} aria-hidden />
                <span>
                  {formatDateTime(
                    dayjs(validUntil).toDate(),
                    DateTimeFormatStyle.MediumDate
                  )}
                </span>
              </div>
            </div>
          )}
          <div className={cn("section")}>
            <h3 className={cn("section-title")}>
              {t("bonusEarnBanner.dialog.offerDetails.title")}
            </h3>
            <p className={cn("section-text")}>
              {/* <0> wraps the "3X*" asterisk; it anchors the desktop-only cap disclaimer so it is hidden on mobile. */}
              {offerEndDate != null ? (
                <Trans
                  i18nKey="bonusEarnBanner.dialog.offerDetails.body"
                  components={[<span className={cn("desktop-only")} />]}
                  values={{
                    earnRate: bonusPointsEarnRate,
                    endDate: offerEndDate,
                  }}
                />
              ) : (
                <Trans
                  i18nKey="bonusEarnBanner.dialog.offerDetails.bodyNoEndDate"
                  components={[<span className={cn("desktop-only")} />]}
                  values={{ earnRate: bonusPointsEarnRate }}
                />
              )}
            </p>
            <p className={cn("section-text")}>
              {t("bonusEarnBanner.dialog.offerDetails.secondary")}
            </p>
          </div>
          {/* Mobile keeps the short T&C line; desktop shows the cap disclaimer. */}
          <p className={cn("footnote", ["mobile-only"])}>
            {t("bonusEarnBanner.dialog.footnote")}
          </p>
          <p className={cn("footnote", ["desktop-only"])}>
            {t("bonusEarnBanner.dialog.capDisclaimer")}
          </p>
        </>
      )}
      howItWorks={(cn) => (
        <>
          <h3 className={cn("how-it-works-title")}>
            {t("bonusEarnBanner.dialog.howItWorks.title")}
          </h3>
          <p className={cn("how-it-works-step")}>
            {t("bonusEarnBanner.dialog.howItWorks.readyToUse")}
          </p>
          <div className={cn("how-it-works-step")}>
            <p>{t("bonusEarnBanner.dialog.howItWorks.payWithEligibleCards")}</p>
            <p>{t("bonusEarnBanner.dialog.howItWorks.eligibleCards")}</p>
          </div>
        </>
      )}
      ctaRow={
        screen === OfferScreen.home
          ? (cn) => (
              <Button
                className={cn("cta")}
                buttonType={ButtonTypeEnum.Primary}
                onClick={handleSearchHotels}
              >
                {t("bonusEarnBanner.dialog.cta")}
              </Button>
            )
          : undefined
      }
      terms={{
        label: t("bonusEarnBanner.dialog.termsAndConditions"),
        content: (cn) => (
          <Trans
            parent={null}
            i18nKey="bonusEarnBanner.dialog.termsAndConditionsBody"
            components={[<p className={cn("terms-paragraph")} />]}
          />
        ),
      }}
    />
  );
};
